import { useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, CheckCircle2, AlertTriangle, FileDown } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

/* Generic CSV import dialog.
 *
 * Props:
 *   open               — controls visibility
 *   onClose            — close handler
 *   onComplete         — called after a successful import (parent should reload)
 *   title              — dialog title (e.g. "Bulk import Parks")
 *   endpoint           — admin API path relative to /admin (e.g. "/assets/parks/import")
 *   templateFilename   — file name for the downloaded CSV template
 *   templateHeaders    — array of CSV column headers for the template download
 *   templateExampleRow — array (same length as templateHeaders) of an example row
 *   testid             — root data-testid prefix
 */
export default function CsvImportDialog({
  open,
  onClose,
  onComplete,
  title,
  endpoint,
  templateFilename,
  templateHeaders,
  templateExampleRow,
  testid = "csv-import-dialog",
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const downloadTemplate = () => {
    const lines = [
      templateHeaders.join(","),
      (templateExampleRow || []).map((v) => (v == null ? "" : String(v))).join(","),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const ok = data.created || 0;
      const skip = data.skipped || 0;
      const errs = (data.errors || []).length;
      toast.success(`Imported ${ok} · skipped ${skip}${errs ? ` · ${errs} errors` : ""}`);
      onComplete?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative bg-white rounded-xl border border-[#E2DCD0] shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <header className="px-5 py-4 border-b border-[#E2DCD0] bg-[#F7F4ED]/60 flex items-center justify-between">
          <h3 className="font-display text-xl text-[#071A33]">{title}</h3>
          <button onClick={handleClose} className="text-[#475569] hover:text-[#071A33]" data-testid={`${testid}-close`}>
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4 overflow-y-auto">
          <button
            type="button"
            onClick={downloadTemplate}
            className="inline-flex items-center gap-2 text-sm text-[#0F766E] hover:underline"
            data-testid={`${testid}-template-download`}
          >
            <FileDown size={14} /> Download CSV template
          </button>

          <div className="rounded-lg border border-dashed border-[#E2DCD0] bg-[#F7F4ED]/40 p-5 text-center">
            <Upload size={22} className="inline text-[#0F766E] mb-2" />
            <p className="text-sm text-[#071A33] font-medium">Choose a CSV file</p>
            <p className="text-xs text-[#475569] mt-1">
              Headers required: <span className="font-mono">{templateHeaders.join(", ")}</span>
            </p>
            <label className="inline-flex items-center gap-1.5 mt-3 text-sm bg-[#064E2E] text-white px-4 py-2 rounded-md hover:bg-[#0a5b55] cursor-pointer">
              <Upload size={13} /> Select file
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid={`${testid}-file-input`}
              />
            </label>
            {file && (
              <p className="mt-2 text-xs text-[#071A33] font-mono" data-testid={`${testid}-filename`}>
                {file.name}
              </p>
            )}
          </div>

          {result && (
            <div className="border border-[#E2DCD0] rounded-lg p-4 bg-white" data-testid={`${testid}-result`}>
              <div className="grid grid-cols-3 gap-3 text-center">
                <ResultStat label="Created" value={result.created} tone="green" />
                <ResultStat label="Skipped" value={result.skipped} tone="amber" />
                <ResultStat label="Errors" value={(result.errors || []).length} tone="red" />
              </div>
              {(result.errors || []).length > 0 && (
                <div className="mt-3 max-h-44 overflow-y-auto text-xs">
                  <p className="text-[10px] tracking-[0.18em] uppercase text-[#B91C1C] font-semibold mb-1.5">Row errors</p>
                  <ul className="space-y-1">
                    {result.errors.slice(0, 50).map((e, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <AlertTriangle size={11} className="text-[#B91C1C] mt-0.5 shrink-0" />
                        <span className="text-[#475569]">
                          Row {e.row}: <span className="text-[#071A33]">{e.reason}</span>
                        </span>
                      </li>
                    ))}
                    {result.errors.length > 50 && (
                      <li className="text-[#475569] italic">…and {result.errors.length - 50} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-[#E2DCD0] bg-[#F7F4ED]/40 flex items-center justify-end gap-2">
          <button type="button" onClick={handleClose} className="text-sm px-4 py-2 rounded-md text-[#475569] hover:bg-white">
            Close
          </button>
          <button
            type="button"
            onClick={upload}
            disabled={!file || busy}
            className="text-sm px-4 py-2 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55] disabled:opacity-50 inline-flex items-center gap-2"
            data-testid={`${testid}-submit`}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
            Import
          </button>
        </footer>
      </div>
    </div>
  );
}

function ResultStat({ label, value, tone }) {
  const cls = tone === "green" ? "text-[#064E2E]" : tone === "amber" ? "text-[#B88A2A]" : "text-[#B91C1C]";
  return (
    <div>
      <p className={`font-display text-3xl tabular-nums ${cls}`}>{value ?? 0}</p>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569] mt-0.5">{label}</p>
    </div>
  );
}
